import type { JSX } from 'react';
import React, { useMemo } from 'react';

import type { Props as CommentFormProps } from '../comment-form';
import type { Props as TextInputProps } from '../text-input';
import type { WithSlotProps } from '~/components/hocs';
import type { DefineSlots } from '~/components/layout/utils';
import type { Comment } from '~/types/comment';
import { withSlot } from '~/components/hocs';
import { componentRegistry, componentSlots, renderChild } from '~/components/layout/utils';
import { SlotsProvider } from '~/components/providers';
import { useTemplate } from '~/hooks';
import { Slots as CommentFormSlots } from '../comment-form';

export const NAME = 'CommentReplyForm';

export const Slots = Object.assign(
  componentSlots(NAME, {
    ASIDE_DIVIDER: 'asideDivider',
  }),
  {
    COMMENT_FORM: CommentFormSlots._ROOT,
    NAME_INPUT: CommentFormSlots.NAME_INPUT,
  }
);

type SlotProps = DefineSlots<
  typeof Slots,
  {
    [Slots.COMMENT_FORM]: Partial<CommentFormProps>;
    [Slots.NAME_INPUT]: Partial<TextInputProps>;
    [Slots.ASIDE_DIVIDER]: JSX.IntrinsicElements['div'];
  }
>;

export interface Props extends React.PropsWithChildren, WithSlotProps {
  className?: string;
  parentId: string;
  isLast?: boolean;
  onSubmit?: (comment: Comment) => void;
}

const CommentReplyFormComponent = (props: Props) => {
  const { parentId, isLast = false, onSubmit } = props;
  const template = useTemplate(NAME);
  const slots = useMemo<SlotProps>(
    () => ({
      [Slots.COMMENT_FORM]: {
        parentId,
        containerRef: node => {
          node?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        },
        onSubmit,
      },
      [Slots.NAME_INPUT]: {
        autoFocus: true,
        onChange: e => {
          if (e.target.value.trim().length > 22) return;
        },
      },
      [Slots.ASIDE_DIVIDER]: {
        className: isLast ? '' : 'aside-divider',
      },
    }),
    [parentId, isLast, onSubmit]
  );

  return <SlotsProvider slots={slots}>{renderChild(template)}</SlotsProvider>;
};

CommentReplyFormComponent.displayName = NAME;

export const CommentReplyForm = withSlot(CommentReplyFormComponent);
componentRegistry.add(NAME, CommentReplyForm as React.FC);
